import { useEffect, useState } from 'react';
import { useUrlFilters } from '../../../hooks/admin/useUrlFilters';
import { useAuthEvents } from '../../../hooks/admin/useAdminSecurity';
import { enumField, pageField, stringField } from '../../../lib/adminFilters';
import { getApiErrorMessage } from '../../../lib/apiError';
import { AUTH_EVENT_TYPES } from '../../../lib/monitoringTypes';
import { formatDateTime } from '../../../lib/utils';
import ExportJsonButton from '../../../components/admin/ExportJsonButton';
import DataTable from '../../../components/ui/DataTable';
import { collectAuthEventsExport, parseSecurityHours, securityExportFilename, windowRange } from '../../../lib/adminSecurityExport';
import Pagination from '../../../components/admin/Pagination';
import InlineNotice from '../../../components/admin/InlineNotice';
import { Pill } from '../../../components/admin/StatusBadge';
import type { AuthEvent } from '../../../types/admin';

const PAGE_SIZE = 25;

const authSchema = {
  email: stringField('', { maxLength: 200 }),
  ip: stringField('', { maxLength: 64 }),
  userId: stringField('', { maxLength: 64 }),
  type: stringField(''),
  success: enumField(['all', 'ok', 'failed'], 'all'),
  from: stringField(''),
  to: stringField(''),
  page: pageField(),
};

function toIsoStart(date: string): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toIsoEnd(date: string): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function TypePill({ event }: { event: AuthEvent }) {
  const failure = !event.success || /FAILED|BLOCKED|REJECTED|RATE_LIMITED/.test(event.type);
  return (
    <Pill tone={failure ? 'danger' : 'pos'} title={event.reason ?? undefined}>
      {event.type}
    </Pill>
  );
}

export default function AuthEventsTab() {
  const { filters, setFilters, resetFilters, searchParams } = useUrlFilters(authSchema);
  const hours = parseSecurityHours(searchParams.get('hours'));
  const [emailInput, setEmailInput] = useState(filters.email);
  const [ipInput, setIpInput] = useState(filters.ip);
  useEffect(() => setEmailInput(filters.email), [filters.email]);
  useEffect(() => setIpInput(filters.ip), [filters.ip]);
  useEffect(() => {
    if (emailInput === filters.email && ipInput === filters.ip) return;
    const t = setTimeout(() => setFilters({ email: emailInput.trim(), ip: ipInput.trim() }), 300);
    return () => clearTimeout(t);
  }, [emailInput, ipInput, filters.email, filters.ip, setFilters]);

  const query = useAuthEvents({
    email: filters.email || undefined,
    ip: filters.ip || undefined,
    userId: filters.userId || undefined,
    type: filters.type || undefined,
    success: filters.success === 'ok' ? '1' : filters.success === 'failed' ? '0' : undefined,
    from: toIsoStart(filters.from),
    to: toIsoEnd(filters.to),
    page: filters.page,
    limit: PAGE_SIZE,
  });

  const rows = query.data?.items ?? [];
  const hasFilters = Boolean(filters.email || filters.ip || filters.userId || filters.type || filters.from || filters.to || filters.success !== 'all');

  return (
    <div className="space-y-4">
      <div className="glass-card space-y-3 p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="Email contains" className="glass-input w-full px-3 py-2 text-sm" />
          <input value={ipInput} onChange={(e) => setIpInput(e.target.value)} placeholder="IP address" className="glass-input w-full px-3 py-2 font-mono text-sm" />
          <select value={filters.type} onChange={(e) => setFilters({ type: e.target.value })} className="glass-input w-full px-3 py-2 text-sm">
            <option value="">All types</option>
            {AUTH_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            value={filters.success}
            onChange={(e) => setFilters({ success: e.target.value as typeof filters.success })}
            className="glass-input w-full px-3 py-2 text-sm"
          >
            <option value="all">Success + failures</option>
            <option value="ok">Success only</option>
            <option value="failed">Failures only</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-glass-text-2">
          <label className="inline-flex items-center gap-2">
            From
            <input type="date" value={filters.from} onChange={(e) => setFilters({ from: e.target.value })} className="glass-input px-3 py-1.5 text-sm" />
          </label>
          <label className="inline-flex items-center gap-2">
            To
            <input type="date" value={filters.to} onChange={(e) => setFilters({ to: e.target.value })} className="glass-input px-3 py-1.5 text-sm" />
          </label>
          {filters.userId && (
            <span className="inline-flex items-center gap-1 rounded-full border border-glass bg-glass-2 px-2.5 py-1 text-xs">
              User: <span className="font-mono">{filters.userId.slice(0, 12)}…</span>
              <button type="button" className="text-glass-text-3 hover:text-glass-text" onClick={() => setFilters({ userId: '' })} aria-label="Remove user filter">
                ×
              </button>
            </span>
          )}
          {hasFilters && (
            <button type="button" className="text-xs text-accent2 underline-offset-2 hover:underline" onClick={() => resetFilters()}>
              Clear filters
            </button>
          )}
          <span className="ml-auto">
            <ExportJsonButton
              label="Download this tab"
              filename={() => securityExportFilename('auth', hours)}
              collect={async () => {
                const range = windowRange(hours);
                const from = toIsoStart(filters.from) ?? range.from;
                const to = toIsoEnd(filters.to) ?? range.to;
                const result = await collectAuthEventsExport({
                  email: filters.email || undefined,
                  ip: filters.ip || undefined,
                  userId: filters.userId || undefined,
                  type: filters.type || undefined,
                  success: filters.success === 'ok' ? '1' : filters.success === 'failed' ? '0' : undefined,
                  from,
                  to,
                });
                return {
                  exportedAt: new Date().toISOString(),
                  hours,
                  from,
                  to,
                  filters,
                  truncated: result.truncated,
                  total: result.total,
                  items: result.items,
                };
              }}
            />
          </span>
        </div>
      </div>

      {query.isError && <InlineNotice tone="error">{getApiErrorMessage(query.error, 'Failed to load auth events')}</InlineNotice>}

      <div className={`glass-card overflow-hidden ${query.isFetching && !query.isPending ? 'opacity-80' : ''}`}>
        <DataTable<AuthEvent>
          data={rows}
          isLoading={query.isPending}
          emptyMessage="No auth events match these filters."
          columns={[
            { header: 'Time', accessor: (e) => <span className="text-glass-text-2">{formatDateTime(e.createdAt)}</span> },
            { header: 'Type', accessor: (e) => <TypePill event={e} /> },
            {
              header: 'Account',
              accessor: (e) => (
                <div className="max-w-[240px]">
                  <div className="truncate text-glass-text">{e.email || '—'}</div>
                  {e.user?.fullName && <div className="truncate text-xs text-glass-text-3">{e.user.fullName}</div>}
                </div>
              ),
            },
            { header: 'IP', accessor: (e) => <span className="font-mono text-xs text-glass-text-2">{e.ip || '—'}</span> },
            {
              header: 'Reason',
              accessor: (e) => (
                <span className="block max-w-[200px] truncate text-glass-text-2" title={e.reason ?? undefined}>
                  {e.reason || '—'}
                </span>
              ),
            },
            {
              header: 'User agent',
              accessor: (e) => (
                <span className="block max-w-[220px] truncate text-xs text-glass-text-3" title={e.userAgent ?? undefined}>
                  {e.userAgent || '—'}
                </span>
              ),
            },
          ]}
        />
        <Pagination pagination={query.data?.pagination} onPageChange={(page) => setFilters({ page })} label="events" isFetching={query.isFetching && !query.isPending} />
      </div>
    </div>
  );
}
